import { useState, useRef, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Avatar, AvatarFallback } from './ui/avatar';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import { DocumentViewer } from './DocumentViewer';
import { SessionMemory } from './SessionMemory';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from './ui/collapsible';
import { 
  Send, 
  Bot, 
  User, 
  ThumbsUp, 
  ThumbsDown, 
  Clock, 
  FileText,
  Calendar,
  DollarSign,
  Heart,
  Users,
  AlertCircle,
  ExternalLink,
  MessageSquare,
  ChevronDown,
  ChevronUp,
  Zap
} from 'lucide-react';

interface Message {
  id: string;
  type: 'user' | 'bot';
  content: string;
  timestamp: Date;
  feedback?: 'positive' | 'negative';
  references?: string[];
  followUpQuestions?: string[];
}

export function AskHR() {
  const [messages, setMessages] = useState<Message[]>([
    {
      id: '1',
      type: 'bot',
      content: "Hello! I'm your AskHR assistant. I can help you with company policies, benefits, time off requests, payroll questions, and more. What would you like to know?",
      timestamp: new Date(),
      references: [],
      followUpQuestions: [
        "What are the most common HR questions?",
        "How do I access my employee benefits?",
        "What should I know as a new employee?"
      ]
    }
  ]);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<string | null>(null);
  const [isDocumentViewerOpen, setIsDocumentViewerOpen] = useState(false);
  const [isQuickSuggestionsCollapsed, setIsQuickSuggestionsCollapsed] = useState(false);
  const [manuallyToggled, setManuallyToggled] = useState(false);
  
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);

  // Mock knowledge base with follow-up questions
  const knowledgeBase = {
    timeoff: {
      keywords: ['time off', 'vacation', 'pto', 'sick leave', 'personal day', 'holiday'],
      responses: [
        "You can request time off through the portal under 'Time Off & Scheduling'. You have 18.5 PTO days remaining. Requests should be submitted at least 2 weeks in advance for approval.",
        "Your current PTO balance is 18.5 days. Sick leave is separate and you have unlimited sick days per company policy. All time off requests require manager approval.",
        "For holiday schedules, please check the company calendar. We observe 10 federal holidays plus 2 floating holidays you can use anytime with approval."
      ],
      references: ['Employee Handbook Section 4.2', 'PTO Policy 2024', 'Holiday Schedule'],
      followUpQuestions: [
        "How do I check my PTO balance?",
        "What's the process for emergency time off?",
        "Can I cash out unused PTO?",
        "What holidays does Akme observe?"
      ]
    },
    benefits: {
      keywords: ['benefits', 'health insurance', 'dental', 'vision', '401k', 'retirement'],
      responses: [
        "Akme offers comprehensive benefits including health, dental, and vision insurance. You can view your current elections and make changes during open enrollment or qualifying life events.",
        "Your 401k plan includes a 6% company match. Current contribution: 8% of salary. You're eligible for our wellness program with up to $500 annual incentive.",
        "Health insurance options include PPO and HMO plans. Dental and vision are included. Mental health support is available through our EAP program at no cost."
      ],
      references: ['Benefits Guide 2024', 'Open Enrollment Materials', 'Wellness Program Details'],
      followUpQuestions: [
        "When is open enrollment?",
        "How do I change my 401k contribution?",
        "What wellness programs are available?",
        "How do I access mental health support?"
      ]
    },
    payroll: {
      keywords: ['payroll', 'paystub', 'salary', 'direct deposit', 'tax', 'w2'],
      responses: [
        "You can view your paystubs and W2 forms in the 'Payroll & Compensation' section. Direct deposit information can be updated there as well.",
        "Payroll is processed bi-weekly on Fridays. Your next payday is January 19th. Tax withholdings can be adjusted by submitting a new W-4 form.",
        "For payroll discrepancies, contact payroll@akme.com or submit a ticket. Include your employee ID and pay period dates for faster resolution."
      ],
      references: ['Payroll Policy', 'Tax Withholding Guide', 'Direct Deposit Instructions'],
      followUpQuestions: [
        "How do I update my direct deposit?",
        "Where can I find my W2?",
        "How do I change my tax withholdings?",
        "What if there's an error in my paycheck?"
      ]
    },
    policies: {
      keywords: ['policy', 'procedure', 'harassment', 'dress code', 'remote work', 'conduct'],
      responses: [
        "All company policies are available in the 'Policies & Procedures' section. Key policies include Code of Conduct, Anti-Harassment, and Remote Work Guidelines.",
        "Our dress code policy varies by location and role. Retail locations require business casual with company-approved attire. Corporate offices allow smart casual.",
        "Remote work is available for eligible positions with manager approval. Review the Remote Work Policy for requirements and approval process."
      ],
      references: ['Employee Handbook', 'Code of Conduct', 'Remote Work Policy'],
      followUpQuestions: [
        "What's the dress code for my location?",
        "How do I report harassment?",
        "Can I work remotely?",
        "What are the code of conduct requirements?"
      ]
    },
    training: {
      keywords: ['training', 'learning', 'course', 'certification', 'development'],
      responses: [
        "Required training includes harassment prevention, safety protocols, and role-specific certifications. You have 3 modules remaining in your current training plan.",
        "Professional development opportunities include LinkedIn Learning, internal workshops, and conference attendance. Request approval through your manager.",
        "Compliance training must be completed annually. You can access courses through the Learning Management System in the portal."
      ],
      references: ['Training Requirements', 'Professional Development Policy', 'LMS User Guide'],
      followUpQuestions: [
        "What training do I need to complete?",
        "How do I access LinkedIn Learning?",
        "Can I attend external conferences?",
        "What's the deadline for compliance training?"
      ]
    }
  };

  const quickSuggestions = [
    { text: "How do I request time off?", icon: Calendar },
    { text: "What are my benefits?", icon: Heart },
    { text: "Where can I find my paystub?", icon: DollarSign },
    { text: "What's the dress code policy?", icon: FileText },
    { text: "How do I update my emergency contact?", icon: Users },
    { text: "What training do I need to complete?", icon: AlertCircle }
  ];

  const generateResponse = (userInput: string): { content: string; references: string[]; followUpQuestions: string[] } => {
    const lowerInput = userInput.toLowerCase();
    
    for (const [category, data] of Object.entries(knowledgeBase)) {
      if (data.keywords.some(keyword => lowerInput.includes(keyword))) {
        const randomResponse = data.responses[Math.floor(Math.random() * data.responses.length)] || '';
        return {
          content: randomResponse,
          references: data.references,
          followUpQuestions: data.followUpQuestions
        };
      }
    }

    // Default response for unrecognized queries
    return {
      content: "I'm not sure about that specific question. You can:\n• Contact HR directly at hr@akme.com\n• Search the employee handbook\n• Submit a support ticket\n• Try rephrasing your question\n\nIs there something else I can help you with?",
      references: ['Employee Handbook', 'HR Contact Information'],
      followUpQuestions: [
        "What are the most common HR questions?",
        "How do I contact HR directly?",
        "What services does HR provide?"
      ]
    };
  };

  // Check if chat content requires scrolling
  const checkScrollNeed = () => {
    if (messagesContainerRef.current && chatContainerRef.current && !manuallyToggled) {
      const container = chatContainerRef.current;
      const content = messagesContainerRef.current;
      
      // Check if content height exceeds container height
      const needsScroll = content.scrollHeight > container.clientHeight;
      
      if (needsScroll && !isQuickSuggestionsCollapsed) {
        setIsQuickSuggestionsCollapsed(true);
      } else if (!needsScroll && isQuickSuggestionsCollapsed && messages.length <= 2) {
        // Only auto-expand if we have minimal messages
        setIsQuickSuggestionsCollapsed(false);
      }
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSend = async (text?: string) => {
    const messageText = text || input || '';
    if (!messageText.trim()) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      type: 'user',
      content: messageText,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsTyping(true);

    // Scroll to bottom after adding user message
    setTimeout(scrollToBottom, 100);

    // Simulate typing delay
    setTimeout(() => {
      const { content, references, followUpQuestions } = generateResponse(messageText);
      const botMessage: Message = {
        id: (Date.now() + 1).toString(),
        type: 'bot',
        content,
        timestamp: new Date(),
        references,
        followUpQuestions
      };

      setMessages(prev => [...prev, botMessage]);
      setIsTyping(false);
      
      // Scroll to bottom after adding bot message
      setTimeout(scrollToBottom, 100);
    }, 1000 + Math.random() * 1000);
  };

  const handleFeedback = (messageId: string, feedback: 'positive' | 'negative') => {
    setMessages(prev => prev.map(msg => 
      msg.id === messageId ? { ...msg, feedback } : msg
    ));
  };

  const handleReferenceClick = (reference: string) => {
    setSelectedDocument(reference);
    setIsDocumentViewerOpen(true);
  };

  const handleFollowUpClick = (question: string) => {
    handleSend(question);
  };

  const handleToggleQuickSuggestions = () => {
    setIsQuickSuggestionsCollapsed(!isQuickSuggestionsCollapsed);
    setManuallyToggled(true);
    
    // Reset manual toggle after 5 seconds to allow auto-detection again
    setTimeout(() => {
      setManuallyToggled(false);
    }, 5000);
  };

  // Session management functions
  const handleRestoreSession = (sessionMessages: Message[]) => {
    setMessages(sessionMessages);
    setTimeout(scrollToBottom, 100);
  };

  const handleNewSession = () => {
    setMessages([
      {
        id: Date.now().toString(),
        type: 'bot',
        content: "Hello! I'm your AskHR assistant. I can help you with company policies, benefits, time off requests, payroll questions, and more. What would you like to know?",
        timestamp: new Date(),
        references: [],
        followUpQuestions: [
          "What are the most common HR questions?",
          "How do I access my employee benefits?",
          "What should I know as a new employee?"
        ]
      }
    ]);
    setTimeout(scrollToBottom, 100);
  };

  // Effect to check scroll need when messages change
  useEffect(() => {
    setTimeout(checkScrollNeed, 100);
  }, [messages, isTyping]);

  // Effect to setup ResizeObserver
  useEffect(() => {
    if (!chatContainerRef.current || !messagesContainerRef.current) return;

    const resizeObserver = new ResizeObserver(() => {
      checkScrollNeed();
    });

    resizeObserver.observe(chatContainerRef.current);
    resizeObserver.observe(messagesContainerRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, [messages]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  return (
    <div className="h-full flex flex-col max-w-6xl mx-auto space-y-4 overflow-hidden relative">
      {/* Quick suggestions - Collapsible */}
      <Collapsible open={!isQuickSuggestionsCollapsed} className="flex-shrink-0">
        <Card className="transition-all duration-300 ease-in-out">
          <CollapsibleTrigger asChild>
            <CardHeader className="cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="bg-blue-600 text-white p-2 rounded-full">
                    <Bot className="h-6 w-6" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <div className="flex items-center gap-2">
                        <CardTitle>AskHR Assistant</CardTitle>
                        <span className="text-lg">✨</span>
                      </div>
                      {isQuickSuggestionsCollapsed && (
                        <Badge variant="secondary" className="text-xs">
                          <Zap className="h-3 w-3 mr-1" />
                          Quick Actions
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {isQuickSuggestionsCollapsed 
                        ? "Click to expand quick suggestions" 
                        : "Get instant answers to your HR questions 24/7"
                      }
                    </p>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleToggleQuickSuggestions}
                  className="p-2"
                >
                  {isQuickSuggestionsCollapsed ? (
                    <ChevronDown className="h-4 w-4" />
                  ) : (
                    <ChevronUp className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </CardHeader>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                {quickSuggestions.map((suggestion, index) => {
                  const Icon = suggestion.icon;
                  return (
                    <Button
                      key={index}
                      variant="outline"
                      size="sm"
                      onClick={() => handleSend(suggestion.text)}
                      className="justify-start text-left h-auto p-3 hover:bg-blue-50 dark:hover:bg-blue-900/20"
                    >
                      <Icon className="h-4 w-4 mr-2 flex-shrink-0" />
                      <span className="text-sm truncate">{suggestion.text}</span>
                    </Button>
                  );
                })}
              </div>
            </CardContent>
          </CollapsibleContent>
        </Card>
      </Collapsible>

      {/* Chat container - Flexible height */}
      <Card className="flex-1 flex flex-col min-h-0">
        <CardHeader className="pb-3 flex-shrink-0">
          <div className="flex items-center justify-between">
            <h3 className="font-medium">Chat with AskHR</h3>
            <div className="flex items-center gap-2">
              {isQuickSuggestionsCollapsed && (
                <Badge variant="outline" className="text-xs">
                  More space for chat
                </Badge>
              )}
              <Badge variant="secondary" className="text-xs">
                <div className="w-2 h-2 bg-green-500 rounded-full mr-1"></div>
                Online
              </Badge>
            </div>
          </div>
        </CardHeader>
        
        <CardContent className="flex-1 flex flex-col min-h-0">
          {/* Messages area with proper scrolling */}
          <div className="flex-1 min-h-0 mb-4" ref={chatContainerRef}>
            <ScrollArea className="h-full pr-4">
              <div className="space-y-6" ref={messagesContainerRef}>
                {messages.map((message) => (
                  <div key={message.id} className={`flex gap-3 ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}>
                    {message.type === 'bot' && (
                      <Avatar className="h-8 w-8 flex-shrink-0">
                        <AvatarFallback className="bg-blue-100 text-blue-600">
                          <Bot className="h-4 w-4" />
                        </AvatarFallback>
                      </Avatar>
                    )}
                    
                    <div className={`flex-1 min-w-0 ${message.type === 'user' ? 'max-w-[80%] order-first' : 'max-w-[85%]'}`}>
                      <div className={`p-4 rounded-lg break-words ${
                        message.type === 'user' 
                          ? 'bg-blue-600 text-white ml-auto' 
                          : 'bg-gray-100 dark:bg-gray-800'
                      }`}>
                        <div className="whitespace-pre-wrap text-sm leading-relaxed">{message.content}</div>
                        
                        {/* References */}
                        {message.references && message.references.length > 0 && (
                          <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                            <div className="text-xs text-gray-500 dark:text-gray-400 mb-2">References:</div>
                            <div className="flex flex-wrap gap-1">
                              {message.references.map((ref, index) => (
                                <Button
                                  key={index}
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleReferenceClick(ref)}
                                  className="text-xs h-6 px-2 hover:bg-blue-50 dark:hover:bg-blue-900"
                                >
                                  <FileText className="h-3 w-3 mr-1" />
                                  {ref}
                                  <ExternalLink className="h-3 w-3 ml-1" />
                                </Button>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                      
                      {/* Follow-up questions */}
                      {message.type === 'bot' && message.followUpQuestions && message.followUpQuestions.length > 0 && (
                        <div className="mt-3 space-y-2">
                          <div className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                            <MessageSquare className="h-3 w-3 inline mr-1" />
                            Follow-up questions:
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {message.followUpQuestions.map((question, index) => (
                              <Button
                                key={index}
                                variant="ghost"
                                size="sm"
                                onClick={() => handleFollowUpClick(question)}
                                className="text-xs h-7 px-3 border border-gray-200 dark:border-gray-700 hover:bg-blue-50 dark:hover:bg-blue-900 text-left"
                              >
                                {question}
                              </Button>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {/* Timestamp and feedback */}
                      <div className="flex items-center gap-2 mt-2 text-xs text-gray-500">
                        <Clock className="h-3 w-3" />
                        {message.timestamp.toLocaleTimeString()}
                        
                        {message.type === 'bot' && (
                          <div className="flex items-center gap-1 ml-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleFeedback(message.id, 'positive')}
                              className={`h-6 w-6 p-0 ${message.feedback === 'positive' ? 'text-green-600' : ''}`}
                            >
                              <ThumbsUp className="h-3 w-3" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleFeedback(message.id, 'negative')}
                              className={`h-6 w-6 p-0 ${message.feedback === 'negative' ? 'text-red-600' : ''}`}
                            >
                              <ThumbsDown className="h-3 w-3" />
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                    
                    {message.type === 'user' && (
                      <Avatar className="h-8 w-8 flex-shrink-0">
                        <AvatarFallback className="bg-purple-100 text-purple-600">
                          <User className="h-4 w-4" />
                        </AvatarFallback>
                      </Avatar>
                    )}
                  </div>
                ))}
                
                {isTyping && (
                  <div className="flex gap-3">
                    <Avatar className="h-8 w-8 flex-shrink-0">
                      <AvatarFallback className="bg-blue-100 text-blue-600">
                        <Bot className="h-4 w-4" />
                      </AvatarFallback>
                    </Avatar>
                    <div className="bg-gray-100 dark:bg-gray-800 p-4 rounded-lg">
                      <div className="flex gap-1">
                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Invisible element for scrolling */}
                <div ref={messagesEndRef} />
              </div>
            </ScrollArea>
          </div>
          
          {/* Input area - Fixed at bottom */}
          <div className="flex gap-2 pt-4 border-t flex-shrink-0">
            <Input
              placeholder="Type your question..."
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && handleSend()}
              className="flex-1"
            />
            <Button onClick={() => handleSend()} disabled={!input.trim()}>
              <Send className="h-4 w-4" />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Session Memory Panel */}
      <SessionMemory
        onRestoreSession={handleRestoreSession}
        currentMessages={messages}
        onNewSession={handleNewSession}
      />

      {/* Document Viewer */}
      <DocumentViewer
        isOpen={isDocumentViewerOpen}
        onClose={() => setIsDocumentViewerOpen(false)}
        documentTitle={selectedDocument || ''}
      />
    </div>
  );
}